import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
    Container, Box, Typography, Paper, Grid, Tabs, Tab, Button, Avatar,
    Chip, CircularProgress, IconButton, Tooltip, TextField, Dialog,
    DialogTitle, DialogContent, DialogActions, useTheme, useMediaQuery,
} from '@mui/material';
import {
    Bolt as BoltIcon, Share as ShareIcon, EmojiEvents as TrophyIcon,
    ThumbUp as RespectIcon, Extension as ChallengeIcon, Lightbulb as InspireIcon,
    Star as NiceFlexIcon, MoreVert as MoreIcon, Flag as FlagIcon,
    Delete as DeleteIcon, Person as PersonIcon,
} from '@mui/icons-material';
import { flexFeedAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { trackFlexEvent, FLEX_EVENTS } from '../utils/flexAnalytics';

const FILTERS = [
    { value: 'global', label: 'All Posts' },
    { value: 'challenges', label: 'Challenges' },
    { value: 'cards', label: 'FLEX Cards' },
    { value: 'badges', label: 'Badges' },
    { value: 'progress', label: 'Progress' },
];

const REACTIONS = [
    { type: 'respect', icon: RespectIcon, label: 'Respect', color: '#00BFFF' },
    { type: 'challenge_accepted', icon: ChallengeIcon, label: 'Challenge Accepted', color: '#FF9800' },
    { type: 'inspiring', icon: InspireIcon, label: 'Inspiring', color: '#8DC63F' },
    { type: 'nice_flex', icon: NiceFlexIcon, label: 'Nice FLEX', color: '#FFD700' },
];

const POST_TYPE_META = {
    card_share: { label: 'FLEX Card', color: '#FFD700', icon: BoltIcon },
    challenge_result: { label: 'Challenge', color: '#FF9800', icon: TrophyIcon },
    badge_unlock: { label: 'Badge', color: '#8DC63F', icon: TrophyIcon },
    milestone: { label: 'Milestone', color: '#00BFFF', icon: BoltIcon },
    streak: { label: 'Streak', color: '#FF5722', icon: BoltIcon },
    manual: { label: 'Post', color: '#9C27B0', icon: PersonIcon },
};

const FeedPostCard = ({ post, onReact, onReport, onDelete, currentUser }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const typeMeta = POST_TYPE_META[post.type] || POST_TYPE_META.manual;
    const TypeIcon = typeMeta.icon;
    const isOwn = currentUser && post.user?._id === currentUser._id;

    return (
        <Paper elevation={0} sx={{
            p: { xs: 2, sm: 3 }, borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            mb: 2, transition: 'all 0.2s',
            '&:hover': { borderColor: alpha(theme.palette.primary.main, 0.3) },
        }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                <Avatar
                    src={post.user?.profile?.avatarUrl}
                    sx={{ width: 40, height: 40, cursor: 'pointer', flexShrink: 0 }}
                    onClick={() => navigate(`/flex/${post.user?.profile?.displayName}`)}
                >
                    {post.user?.profile?.displayName?.[0]?.toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, cursor: 'pointer' }}
                            onClick={() => navigate(`/flex/${post.user?.profile?.displayName}`)}
                        >
                            @{post.user?.profile?.displayName}
                        </Typography>
                        <Chip
                            icon={<TypeIcon sx={{ fontSize: 14, color: typeMeta.color }} />}
                            label={typeMeta.label}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        {new Date(post.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </Typography>
                </Box>
                {(isOwn || !isOwn) && (
                    <Box>
                        <IconButton size="small" onClick={() => setMenuOpen(!menuOpen)}>
                            <MoreIcon fontSize="small" />
                        </IconButton>
                        {menuOpen && (
                            <Box sx={{ position: 'absolute', zIndex: 10, mt: 1 }}>
                                <Paper elevation={2} sx={{ p: 0.5 }}>
                                    {isOwn ? (
                                        <Button size="small" startIcon={<DeleteIcon />} color="error" onClick={() => { onDelete(post._id); setMenuOpen(false); }}>
                                            Delete
                                        </Button>
                                    ) : (
                                        <Button size="small" startIcon={<FlagIcon />} color="error" onClick={() => { onReport(post._id); setMenuOpen(false); }}>
                                            Report
                                        </Button>
                                    )}
                                </Paper>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {/* Content */}
            <Typography variant="body1" sx={{ fontWeight: 600, mb: post.body ? 1 : 0 }}>
                {post.title}
            </Typography>
            {post.body && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {post.body}
                </Typography>
            )}

            {/* Stats badges */}
            {(post.wpm || post.comprehension || post.effectiveWpm) && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    {post.wpm && <Chip label={`${post.wpm} WPM`} size="small" sx={{ bgcolor: '#FFD700', color: '#000' }} />}
                    {post.comprehension && <Chip label={`${post.comprehension}% comp`} size="small" sx={{ bgcolor: '#8DC63F', color: '#000' }} />}
                    {post.effectiveWpm && <Chip label={`${post.effectiveWpm} eff WPM`} size="small" sx={{ bgcolor: '#00BFFF', color: '#000' }} />}
                </Box>
            )}

            {/* Card link */}
            {post.flexCard && (
                <Button size="small" href={`/flex/card/${post.flexCard}`} sx={{ mb: 1, textTransform: 'none' }}>
                    View FLEX Card →
                </Button>
            )}

            {/* Reactions */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {REACTIONS.map(r => {
                    const count = post.reactionCounts?.[r.type] || 0;
                    const Icon = r.icon;
                    return (
                        <Tooltip key={r.type} title={r.label}>
                            <Chip
                                icon={<Icon sx={{ fontSize: 16, color: count > 0 ? r.color : 'text.disabled' }} />}
                                label={count > 0 ? count : ''}
                                size="small"
                                variant={count > 0 ? 'outlined' : 'filled'}
                                onClick={() => onReact(post._id, r.type)}
                                sx={{ cursor: 'pointer', height: 28 }}
                            />
                        </Tooltip>
                    );
                })}
            </Box>
        </Paper>
    );
};

function alpha(color, value) {
    return require('@mui/material/styles').alpha(color, value);
}

const FlexFeed = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const { user } = useAuth();

    const [filter, setFilter] = useState('global');
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ pages: 1, total: 0 });
    const [composeOpen, setComposeOpen] = useState(false);
    const [composeTitle, setComposeTitle] = useState('');
    const [composeBody, setComposeBody] = useState('');
    const [composeError, setComposeError] = useState('');
    const [composing, setComposing] = useState(false);

    const fetchFeed = useCallback(async (pageNum = 1) => {
        try {
            setLoading(true);
            const data = await flexFeedAPI.getFeed(filter, pageNum, 20);
            setPosts(data.posts);
            setPagination(data.pagination);
            setPage(pageNum);
        } catch (err) {
            console.error('Failed to fetch feed:', err);
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        fetchFeed(1);
        trackFlexEvent(FLEX_EVENTS.FEED_VIEWED, { filter });
    }, [fetchFeed]);

    const handleReact = async (postId, reactionType) => {
        if (!user) { navigate('/login'); return; }
        try {
            const result = await flexFeedAPI.react(postId, reactionType);
            setPosts(prev => prev.map(p =>
                p._id === postId ? { ...p, reactionCounts: result.reactionCounts } : p
            ));
        } catch (err) {
            console.error('Failed to react:', err);
        }
    };

    const handleReport = async (postId) => {
        try {
            await flexFeedAPI.report(postId);
        } catch (err) {
            console.error('Failed to report:', err);
        }
    };

    const handleDelete = async (postId) => {
        try {
            await flexFeedAPI.delete(postId);
            setPosts(prev => prev.filter(p => p._id !== postId));
        } catch (err) {
            console.error('Failed to delete:', err);
        }
    };

    const handleCompose = async () => {
        if (!composeTitle.trim()) {
            setComposeError('Title is required.');
            return;
        }
        try {
            setComposing(true);
            setComposeError('');
            await flexFeedAPI.createPost({ title: composeTitle, body: composeBody, type: 'manual' });
            setComposeOpen(false);
            setComposeTitle('');
            setComposeBody('');
            fetchFeed(1);
        } catch (err) {
            setComposeError(err.response?.data?.error || 'Failed to post.');
        } finally {
            setComposing(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>FLEX Feed | ReaderFLEX</title>
                <meta name="description" content="See what the ReaderFLEX community is achieving — FLEX Cards, challenge results, badges, and milestones." />
            </Helmet>

            <Container maxWidth="sm" sx={{ mt: 4, mb: 8 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <BoltIcon sx={{ color: '#FFD700' }} />
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        FLEX Feed
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Community reading achievements. React with respect, challenge accepted, inspiring, or nice FLEX.
                </Typography>

                {/* Compose button */}
                {user && (
                    <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<BoltIcon />}
                        onClick={() => setComposeOpen(true)}
                        sx={{ mb: 3, minHeight: 44 }}
                    >
                        Share to FLEX Feed
                    </Button>
                )}

                {/* Filter tabs */}
                <Tabs
                    value={filter}
                    onChange={(_, v) => setFilter(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ mb: 3 }}
                >
                    {FILTERS.map(f => (
                        <Tab key={f.value} value={f.value} label={f.label} />
                    ))}
                </Tabs>

                {/* Feed */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : posts.length === 0 ? (
                    <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                            No posts yet. Be the first to share!
                        </Typography>
                        {user ? (
                            <Button variant="contained" onClick={() => setComposeOpen(true)}>
                                Share to Feed
                            </Button>
                        ) : (
                            <Button variant="contained" href="/register">
                                Join ReaderFLEX
                            </Button>
                        )}
                    </Paper>
                ) : (
                    <>
                        {posts.map(post => (
                            <FeedPostCard
                                key={post._id}
                                post={post}
                                onReact={handleReact}
                                onReport={handleReport}
                                onDelete={handleDelete}
                                currentUser={user}
                            />
                        ))}

                        {/* Pagination */}
                        {pagination.pages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3 }}>
                                <Button
                                    size="small"
                                    disabled={page <= 1}
                                    onClick={() => fetchFeed(page - 1)}
                                >
                                    Previous
                                </Button>
                                <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                                    Page {page} of {pagination.pages}
                                </Typography>
                                <Button
                                    size="small"
                                    disabled={page >= pagination.pages}
                                    onClick={() => fetchFeed(page + 1)}
                                >
                                    Next
                                </Button>
                            </Box>
                        )}
                    </>
                )}

                {/* Compose Dialog */}
                <Dialog open={composeOpen} onClose={() => setComposeOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Share to FLEX Feed</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Title"
                            value={composeTitle}
                            onChange={(e) => setComposeTitle(e.target.value)}
                            sx={{ mb: 2, mt: 1 }}
                            inputProps={{ maxLength: 200 }}
                        />
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Message (optional)"
                            value={composeBody}
                            onChange={(e) => setComposeBody(e.target.value)}
                            inputProps={{ maxLength: 1000 }}
                        />
                        {composeError && (
                            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                {composeError}
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setComposeOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={handleCompose} disabled={composing}>
                            {composing ? <CircularProgress size={20} /> : 'Post'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </>
    );
};

export default FlexFeed;
